import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Plane, Hotel, Star, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingModal from '../../components/BookingModal';

const GrandPrixMonaco = () => {
  const { language } = useLanguage();
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Données du séjour pour le modal
  const stayData = {
    id: '7f8f1f9f-f584-4669-a2bd-e9ef06b4575a',
    title: 'Grand Prix de Monaco',
    title_en: 'Monaco Grand Prix',
    date: '5-7 Juin 2026',
    location: 'Monte-Carlo, Monaco',
    price_member_with_flight: 12500,
    price_member_no_flight: 8500,
    price_non_member_with_flight: 15000,
    price_non_member_no_flight: 10000,
    total_seats: 40,
    seats_sold: 0
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src="https://customer-assets.emergentagent.com/job_4a16d148-a58f-4dee-a32d-adc2ae4e5015/artifacts/c5ryn3ba_GP%20MONACO%202026.jpg"
            alt="Grand Prix Monaco"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(255, 254, 242, 0.9))'
          }} />
        </div>

        <div className="container" style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <Link to="/member/stays" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '8px 16px',
            background: 'rgba(255, 254, 242, 0.9)',
            border: '1px solid var(--border-light)'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour aux séjours' : 'Back to stays'}
          </Link>
          
          <h1 className="hero-large" style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
            {language === 'fr' ? 'Séjour Grand Prix de Monaco' : 'Monaco Grand Prix Stay'}
          </h1>
          <h2 className="hero-medium" style={{ 
            marginBottom: '24px',
            color: 'var(--text-secondary)'
          }}>
            {language === 'fr' ? '79ème Grand Prix de Monaco' : '79th Monaco Grand Prix'}
          </h2>
          
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} />
              <span className="body-large">23-25 Mai 2026</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPin size={20} />
              <span className="body-large">Monaco</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <p className="body-large" style={{
            lineHeight: 1.8,
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            {language === 'fr' 
              ? "Le Club Henry & Gaby vous propose un week-end prestigieux et une occasion de vous y rendre en jet privé, et d'assister en VIP, dans un cadre magnifique, au plus mythique des Grands Prix de Formule 1."
              : "The Henry & Gaby Club offers you a prestigious weekend and an opportunity to get there by private jet and attend in VIP style, in a magnificent setting, the most legendary Formula 1 Grand Prix."}
          </p>
          
          <h3 className="heading-1" style={{ marginBottom: '16px', textAlign: 'center' }}>
            {language === 'fr' ? 'Présentation du Package H&G – Monaco 2026' : 'H&G Package Presentation – Monaco 2026'}
          </h3>
          
          <p className="body-regular" style={{
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            {language === 'fr'
              ? "Le Grand Prix de Monaco constitue l'un des événements les plus emblématiques du calendrier international. À cette occasion, H&G propose une solution d'hospitalité haut de gamme, conçue pour répondre aux standards les plus élevés du secteur et offrir un cadre propice aux relations d'affaires et aux rencontres de haut niveau."
              : "The Monaco Grand Prix is one of the most iconic events on the international calendar. On this occasion, H&G offers a high-end hospitality solution, designed to meet the highest standards in the sector and provide an environment conducive to business relations and high-level meetings."}
          </p>
          
          <p className="body-regular" style={{
            lineHeight: 1.8,
            color: 'var(--text-secondary)',
            textAlign: 'center'
          }}>
            {language === 'fr'
              ? "Vivez l'un des événements les plus prestigieux au monde dans un cadre d'exception. Monaco est le sommet absolu de l'hospitalité F1. Hospitality (terrasses panoramiques, restauration gastronomique, open bar premium)."
              : "Experience one of the most prestigious events in the world in an exceptional setting. Monaco is the absolute pinnacle of F1 hospitality. Hospitality (panoramic terraces, gourmet dining, premium open bar)."}
          </p>
        </div>
      </section>

      {/* Ce que comprend */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '48px', textAlign: 'center' }}>
            {language === 'fr' ? '✅ Ce que comprend votre expérience H&G' : '✅ What your H&G experience includes'}
          </h2>
          
          <div style={{
            display: 'grid',
            gap: '16px'
          }}>
            {[
              language === 'fr' ? 'Accès VIP sur un rooftop ou terrasse premium avec vue directe sur le circuit' : 'VIP access on a rooftop or premium terrace with direct view of the circuit',
              language === 'fr' ? 'Service traiteur gastronomique tout au long de la journée' : 'Gourmet catering service throughout the day',
              language === 'fr' ? 'Bar premium à discrétion' : 'Premium bar at discretion',
              language === 'fr' ? 'Soirée Prestige H&G dans un lieu exclusif de la Principauté' : 'H&G Prestige Evening in an exclusive location in the Principality',
              language === 'fr' ? 'Accueil personnalisé par nos hôtesses' : 'Personalized welcome by our hostesses',
              language === 'fr' ? 'Cadeau VIP H&G' : 'H&G VIP gift',
              language === 'fr' ? 'Possibilité d\'arrivée en jet privé (Nice ou Monaco Héliport)' : 'Possibility of arrival by private jet (Nice or Monaco Heliport)',
              language === 'fr' ? 'Gestion complète du séjour (transferts, hôtels, restaurants)' : 'Complete stay management (transfers, hotels, restaurants)'
            ].map((item, index) => (
              <div key={index} style={{
                padding: '20px 24px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-light)',
                display: 'flex',
                gap: '16px',
                alignItems: 'center'
              }}>
                <span style={{ color: '#2c5f7d', fontWeight: 'bold', fontSize: '20px' }}>✓</span>
                <span className="body-regular">{item}</span>
              </div>
            ))}
          </div>
          
          <div style={{
            marginTop: '48px',
            padding: '32px',
            background: 'var(--bg-secondary)',
            border: '2px solid var(--text-primary)',
            textAlign: 'center'
          }}>
            <h3 className="heading-1" style={{ marginBottom: '16px' }}>
              {language === 'fr' ? 'Pourquoi Monaco ?' : 'Why Monaco?'}
            </h3>
            <p className="body-regular" style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              {language === 'fr'
                ? "Le Grand Prix le plus glamour du calendrier, fréquenté par les dirigeants, célébrités et décideurs internationaux. Une opportunité unique de networking haut de gamme."
                : "The most glamorous Grand Prix on the calendar, attended by leaders, celebrities and international decision-makers. A unique high-end networking opportunity."}
            </p>
          </div>
        </div>
      </section>

      {/* Prestation VIP */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="hero-medium" style={{ marginBottom: '24px', textAlign: 'center' }}>
            {language === 'fr' ? 'Une Prestation VIP' : 'A VIP Experience'}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginTop: '48px'
          }}>
            <div style={{
              padding: '24px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <Plane size={32} style={{ margin: '0 auto 16px', color: 'var(--text-primary)' }} />
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                {language === 'fr' ? 'Vol Jet Privé' : 'Private Jet Flight'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr'
                  ? 'Paris-Nice en jet privé spécialement affrété'
                  : 'Paris-Nice on specially chartered private jet'}
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '32px', margin: '0 auto 16px' }}>🚁</div>
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                {language === 'fr' ? 'Transfert Hélicoptère' : 'Helicopter Transfer'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr'
                  ? 'Nice-Monaco en hélicoptère'
                  : 'Nice-Monaco by helicopter'}
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <Star size={32} style={{ margin: '0 auto 16px', color: '#d4af37' }} />
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                {language === 'fr' ? 'Accès VIP Circuit' : 'VIP Circuit Access'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr'
                  ? 'Places VIP avec buffet champagne'
                  : 'VIP seats with champagne buffet'}
              </p>
            </div>

            <div style={{
              padding: '24px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <Hotel size={32} style={{ margin: '0 auto 16px', color: 'var(--text-primary)' }} />
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                {language === 'fr' ? 'Hébergement Luxe' : 'Luxury Accommodation'}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                {language === 'fr'
                  ? 'Hôtel 5 étoiles - 2 nuits'
                  : '5-star hotel - 2 nights'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ce qui est inclus / non inclus */}
      <section className="section-padding">
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '32px' }}>
            {/* Inclus */}
            <div style={{
              padding: '32px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)'
            }}>
              <h3 className="heading-1" style={{ marginBottom: '24px', color: '#2c5f7d' }}>
                {language === 'fr' ? 'Cette offre INCLUT' : 'This offer INCLUDES'}
              </h3>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {[
                  language === 'fr' ? 'Vol aller-retour en jet privé Paris-Nice' : 'Round trip private jet Paris-Nice',
                  language === 'fr' ? 'Transfert hélicoptère Nice-Monaco A/R' : 'Helicopter transfer Nice-Monaco round trip',
                  language === 'fr' ? '2 nuits en hôtel 5 étoiles' : '2 nights in 5-star hotel',
                  language === 'fr' ? 'Accès VIP au Grand Prix (dimanche)' : 'VIP access to Grand Prix (Sunday)',
                  language === 'fr' ? 'Buffet champagne sur le circuit' : 'Champagne buffet at the circuit',
                  language === 'fr' ? 'Places assises premium' : 'Premium seated places'
                ].map((item, index) => (
                  <li key={index} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ color: '#2c5f7d', fontWeight: 'bold' }}>✓</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Non inclus */}
            <div style={{
              padding: '32px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)'
            }}>
              <h3 className="heading-1" style={{ marginBottom: '24px', color: '#8b4f6d' }}>
                {language === 'fr' ? 'Cette offre N\'INCLUT PAS' : 'This offer DOES NOT INCLUDE'}
              </h3>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {[
                  language === 'fr' ? 'Repas et boissons hors buffet circuit' : 'Meals and drinks outside circuit buffet',
                  language === 'fr' ? 'Dépenses personnelles' : 'Personal expenses',
                  language === 'fr' ? 'Assurance annulation' : 'Cancellation insurance',
                  language === 'fr' ? 'Activités optionnelles' : 'Optional activities'
                ].map((item, index) => (
                  <li key={index} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ color: '#8b4f6d', fontWeight: 'bold' }}>✗</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Info Members Only */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{
            padding: '32px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}>
            <p className="body-regular" style={{ lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              {language === 'fr'
                ? "Ce séjour est exclusivement dédié aux membres du Club Henry & Gaby et à leurs Compagnons. Les non-adhérents ne sont admis qu'à titre exceptionnel, sur recommandation d'un membre et dans le respect des conditions tarifaires ci-dessus."
                : "This stay is exclusively dedicated to Henry & Gaby Club members and their Companions. Non-members are only admitted exceptionally, upon recommendation of a member and in compliance with the above pricing conditions."}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-small">
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <h3 className="heading-1" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Réservation' : 'Booking'}
          </h3>
          <p className="body-large" style={{ marginBottom: '24px', lineHeight: 1.7 }}>
            {language === 'fr'
              ? 'Réservez votre place pour ce séjour d\'exception'
              : 'Book your place for this exceptional stay'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <button 
              onClick={() => setShowBookingModal(true)} 
              className="btn-primary"
            >
              {language === 'fr' ? 'RÉSERVER' : 'BOOK NOW'}
            </button>
            <Link to="/member/stays" className="btn-secondary">
              {language === 'fr' ? 'Retour aux séjours' : 'Back to stays'}
            </Link>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        item={stayData}
        bookingType="stay"
        onSuccess={() => setShowBookingModal(false)}
      />
    </div>
  );
};

export default GrandPrixMonaco;
